import { Box, BoxProps, FormControl, MenuItem, Popover, Select, Stack } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import { enUS } from 'date-fns/locale';
import { ReactComponent as CalendarIcon } from '../../assets/calendar-outline_white.svg';
import { DateRangeIcon } from 'components/CommonIcons/Icons';
import CheckIcon from '@mui/icons-material/Check';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

type ReactDateRangeFilterProps = {
  startingDate?: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  handleDateSelect: (
    stDt: string,
    edDt: string,
    selectedDate?: string,
    is24HrsTime?: boolean,
  ) => void;
  endingDate?: string;
  boxProps?: BoxProps;
  isDisabled?: boolean;
  isDbuSql?: boolean;
  initialSelectedDate?: string;
  hasYTD?: boolean;
  isNew?: boolean;
};

const timeZone = 'America/Chicago';

const ReactDateRangeFilter = ({
  startingDate,
  endingDate,
  setStartDate,
  setEndDate,
  handleDateSelect,
  isDisabled,
  boxProps,
  isDbuSql = false,
  initialSelectedDate,
  hasYTD = false,
  isNew = false,
}: ReactDateRangeFilterProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const { sx, ...props } = boxProps || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const { projDate } = useParams();
  let dateParam = {
    start: projDate || searchParams.get('startingDate') || startingDate,
    end: projDate || searchParams.get('endingDate') || endingDate,
  };
  const [locale, setLocale] = useState(enUS);
  const [selectionRange, setSelectionRange] = useState<any>({
    startDate: dateParam.start ? new Date(dateParam.start) : new Date(),
    endDate: dateParam.end ? new Date(dateParam.end) : new Date(),
    key: 'selection',
  });
  const [customMonth, setCustomMonth] = useState('');
  const [isDateOpen, setIsDateOpen] = useState(false);

  // Define valid options
  const validOptions = isDbuSql
    ? ['Hrs', '7d', '30d', '3M', 'custom']
    : ['Today', 'Hrs', '7d', '30d', '3M', 'custom'];

  // Helper function to get initial filter month value
  const isIsoDate = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

  // Helper function to get initial filter month value
  const getInitialFilterMonth = () => {
    // Priority 1: Use initialSelectedDate if provided and valid
    if (initialSelectedDate && validOptions.includes(initialSelectedDate)) {
      return initialSelectedDate;
    }

    // Priority 1b: If initialSelectedDate is an explicit ISO date string (clicked single day)
    if (initialSelectedDate && isIsoDate(initialSelectedDate)) {
      return initialSelectedDate;
    }

    // Priority 2: Default based on isDbuSql
    if (isDbuSql) {
      return '3M'; // Default for DbuSql = Last 3 Months
    }

    // Priority 3: Check if dateParam matches today
    const todayDate = moment().tz('America/Chicago').format('YYYY-MM-DD');
    const isToday = moment(dateParam.start).format('YYYY-MM-DD') === todayDate;

    if (isToday) {
      return 'Today';
    }

    // Priority 4: If dateParam.start exists and is not today, check if it should be a custom date
    if (dateParam.start) {
      const dateStr = moment(dateParam.start).format('YYYY-MM-DD');
      // Only use the date string if it's not today's date
      if (dateStr !== todayDate) {
        return dateStr;
      }
    }

    return 'Today'; // Default fallback
  };

  const [filterMonth, setFilterMonth] = useState(getInitialFilterMonth());
  const [filterDefaultMonth, setFilterDefaultMonth] = useState(
    initialSelectedDate && validOptions.includes(initialSelectedDate)
      ? initialSelectedDate
      : isDbuSql
      ? '3M'
      : 'Today',
  );
  const [dateRangeClickCount, setDateRangeClickCount] = useState(0);
  const isCustomMonth = useRef(false);
  const prevFilterMonth = useRef<string | null>(null);
  const [dateFilterMenu, setDateFilterMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // Update filterMonth when initialSelectedDate changes
  useEffect(() => {
    if (!initialSelectedDate) return;
    if (
      (initialSelectedDate && validOptions.includes(initialSelectedDate)) ||
      isIsoDate(initialSelectedDate)
    ) {
      setFilterMonth(initialSelectedDate);
      setFilterDefaultMonth(initialSelectedDate);
    }
  }, [initialSelectedDate]);

  const dateFilterMenuClick = (event: any) => {
    setDateFilterMenu(
      dateFilterMenu === null
        ? {
            mouseX: parseInt(event.clientX) - 400,
            mouseY: parseInt(event.clientY) - 110,
          }
        : null,
    );
  };

  const handleCustomOnclick = (e: any) => {
    const clickedText = e.target.textContent;

    if (clickedText === 'Custom Date Range') {
      prevFilterMonth.current = filterMonth;
      setDateRangeClickCount(0);
      isCustomMonth.current = true;
      setTimeout(() => {
        dateFilterMenuClick(e);
      }, 200);
    } else if (clickedText === 'YTD') {
      // YTD selected - don't open calendar
      isCustomMonth.current = false;
      setIsDateOpen(false);
      return; // Exit early to prevent calendar opening
    }

    if (isCustomMonth.current) setIsDateOpen(true);
    else setIsDateOpen(false);

    if (e.type == 'click' && isDateOpen && e.target.className.includes('MuiModal-backdrop')) {
      isCustomMonth.current = false;
      setDateFilterMenu(null);
      setIsDateOpen(false);
      if (customMonth == '') {
        if (prevFilterMonth.current) {
          setFilterMonth(prevFilterMonth.current);
          prevFilterMonth.current = null;
        } else if (typeof filterMonth === 'string' && filterMonth.includes('-')) {
          // Preserve explicit date
        } else {
          setFilterMonth(filterDefaultMonth);
        }
      }
    }
  };

  const handleDateFilter = (field: any, selectedDate: any) => {
    // dateParam = {start: '', end: ''}
    selectedDate = selectedDate.target.value;
    let stDt = '', edDt = '';
    if (field === 'dateFilter') {
      isCustomMonth.current = false;
      let is24HrsTime = false;
      setCustomMonth('');
      const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
      edDt = isDbuSql
        ? yesterday.endOf('day').format('YYYY-MM-DD HH:mm')
        : moment().endOf('day').format('YYYY-MM-DD HH:mm');

      if (selectedDate == 'Today') {
        stDt = moment().format('YYYY-MM-DD');
      } else if (selectedDate == 'Hrs') {
        stDt = moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm');
        edDt = stDt;
        is24HrsTime = true;
      } else if (selectedDate == '7d') {
        stDt = isDbuSql
          ? yesterday.clone().subtract(6, 'days').format('YYYY-MM-DD HH:mm')
          : moment().subtract(6, 'd').format('YYYY-MM-DD HH:mm');
      } else if (selectedDate == '30d') {
        stDt = isDbuSql
          ? yesterday.clone().subtract(29, 'd').format('YYYY-MM-DD HH:mm')
          : moment().subtract(29, 'd').format('YYYY-MM-DD HH:mm');
      } else if (selectedDate == '3M') {
        stDt = isDbuSql
          ? yesterday.clone().subtract(3, 'M').format('YYYY-MM-DD HH:mm')
          : moment().subtract(3, 'M').format('YYYY-MM-DD HH:mm');
      }
      setStartDate(stDt);
      setEndDate(edDt);

      // Validate the selected date before setting it
      if (validOptions.includes(selectedDate) || selectedDate.includes('-')) {
        setFilterMonth(selectedDate);
      } else {
        // Fallback to default if invalid option
        const defaultValue = isDbuSql ? '3M' : 'Today';
        setFilterMonth(defaultValue);
        selectedDate = defaultValue;
      }

      if (selectedDate === 'custom') {
        if (isDbuSql) {
          // YTD logic
          const startOfYear = moment().startOf('year').format('YYYY-MM-DD');
          const yesterday = moment()
            .tz('Asia/Kolkata')
            .subtract(1, 'day')
            .endOf('day')
            .format('YYYY-MM-DD HH:mm');

          setStartDate(startOfYear);
          setEndDate(yesterday);
          setFilterMonth(selectedDate);
          handleDateSelect(startOfYear, yesterday, selectedDate, true); // true = multiple days
        } else {
          setDateRangeClickCount(0);
          isCustomMonth.current = true;
          setIsDateOpen(true);
        }
        return;
      } else {
        handleDateSelect(
          moment(stDt).format('YYYY-MM-DD'),
          moment(edDt).format('YYYY-MM-DD'),
          selectedDate,
          is24HrsTime,
        );
      }
      setFilterDefaultMonth(selectedDate);
    }
  };

  const handleSelectDateRange = (e: any) => {
    const { startDate, endDate } = e.selection;

    // Always update the selection in state
    setSelectionRange(e.selection);

    // First click → only set fromDate
    if (dateRangeClickCount === 0 && startDate) {
      setDateRangeClickCount(1);
      return; // keep popup open
    }

    // Second click → range complete
    if (dateRangeClickCount === 1 && startDate && endDate) {
      setDateRangeClickCount(0);

    setCustomMonth(e.selection.key);
    handleDateFilterMenuClose(e);
    setIsDateOpen(false);

    const buildChicagoRange = (date: Date, isEnd: boolean) => {
      const base = moment.tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          day: date.getDate(),
        },
        'America/Chicago',
      );

      return (isEnd ? base.endOf('day') : base.startOf('day'))
        .format('YYYY-MM-DD HH:mm');
    };

    const stDt = buildChicagoRange(startDate, false);
    const edDt = buildChicagoRange(endDate, true);

    const isMultipleDate = !moment(stDt, 'YYYY-MM-DD HH:mm').isSame(moment(edDt, 'YYYY-MM-DD HH:mm'),'day');

    handleDateSelect(stDt, edDt, 'custom', isMultipleDate);
  }
  };

  const handleDateFilterMenuClose = (event: any) => {
    setDateFilterMenu(null);
  };

  // Only set default if no initialSelectedDate is provided
  useEffect(() => {
    if (isDbuSql && !initialSelectedDate) {
      const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
      const stDt = yesterday
        .clone()
        .subtract(3, 'months')
        .startOf('day')
        .format('YYYY-MM-DD HH:mm');
      const edDt = yesterday.endOf('day').format('YYYY-MM-DD HH:mm');
      setStartDate(stDt);
      setEndDate(edDt);
      handleDateSelect(moment(stDt).format('YYYY-MM-DD'), moment(edDt).format('YYYY-MM-DD'), '3M');
    }
  }, [isDbuSql]);

  const menuItemSx = {
    padding: '4px 8px',
    ...theme.typography.filterText,
    backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
    color: `${colors.dropdown.menuItemText} !important`,
    '&.Mui-selected': {
      backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
      color: `${colors.dropdown.menuItemHoverText} !important`,
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${colors.dropdown.menuItemHover} !important`,
      color: `${colors.dropdown.menuItemHoverText} !important`,
    },
    '&:hover': {
      backgroundColor: `${colors.dropdown.menuItemHover} !important`,
      color: `${colors.dropdown.menuItemHoverText} !important`,
    },
  };

  const newMenuItemSx = {
    padding: '4px 8px',
    minHeight: '32px',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    color: `${colors.DropdownV2.menuItemText} !important`,
    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
    '&:hover': {
      backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
      color: `${colors.DropdownV2.menuItemHoverText} !important`,
    },
    '&.Mui-focusVisible': {
      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
      color: `${colors.DropdownV2.menuItemHoverText} !important`,
    },
    '&.Mui-selected': {
      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
      color: `${colors.DropdownV2.menuItemHoverText} !important`,
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
      color: `${colors.DropdownV2.menuItemHoverText} !important`,
    },
  };

  const resolvedMenuItemSx = isNew ? newMenuItemSx : menuItemSx;

  const renderMenuLabel = (value: string, label: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 2,
      }}
    >
      <Box component="span">{label}</Box>

      {isNew && filterMonth === value && (
        <CheckIcon
          sx={{
            fontSize: '18px',
            color: `${colors.DropdownV2.tickColor} !important`,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );

  const HeaderDateIcon = ({ className }: { className?: string }) => (
    <Box
      component="span"
      className={className}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!isDisabled) {
          setIsDateOpen(true);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDisabled ? 'default' : 'pointer',
        pointerEvents: 'auto !important',
      }}
    >
      <DateRangeIcon />
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: isNew ? 'transparent' : `${colors.backgroundLight.dark}`,
        borderRadius: '4px',
        fontSize: '15px',
        zIndex: 999,
        width: 'fit-content',
        minWidth: isNew ? 'fit-content' : '230px',
        flexShrink: isNew ? 0 : undefined,
        textAlign: 'left',
        height: isNew ? '100%' : 'fit-content',
        display: isNew ? 'flex' : 'block',
        alignItems: isNew ? 'center' : undefined,
        ...sx,
        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent !important',
        },
        '& .Mui-disabled.MuiSelect-icon': {
          top: isNew ? '50%' : 'auto',
        },
        '& .MuiSelect-icon': {
          top: isNew ? '50%' : 'calc(50% - 12px)',
        },
        ...(isNew && {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
        }),
      }}
      {...props}
    >
      <FormControl
        fullWidth={!isNew}
        sx={{
          width: isNew ? 'fit-content' : '100%',
          minWidth: isNew ? 'fit-content' : undefined,
          flexShrink: isNew ? 0 : undefined,
          height: isNew ? '100%' : 'auto',
          justifyContent: isNew ? 'center' : undefined,
        }}
      >
        <Select
          name="date-select"
          labelId="demo-simple-select-label"
          id="single-date-select"
          value={filterMonth}
          label="Date"
          onChange={(e) => {
            handleDateFilter('dateFilter', e);
          }}
          disabled={isDisabled}
          IconComponent={isNew ? HeaderDateIcon : CalendarIcon}
          open={isDateOpen}
          onClick={(e) => handleCustomOnclick(e)}
          onOpen={() => setIsDateOpen(true)}
          onClose={() => setIsDateOpen(false)}
          sx={{
            '.MuiSelect-select': {
              ...theme.typography.filterText, // apply custom variant
              ...(isNew && {
                color: '#48B5ED',
                fontSize: '14px !important',
                fontWeight: '500 !important',
                fontFamily: 'Montserrat',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minHeight: 'unset !important',
                py: '0 !important',
                pl: '0 !important',
                pr: '24px !important',
                display: 'flex',
                alignItems: 'center',
              }),
            },
            '.MuiSelect-icon': {
              transform: isNew
                ? 'translateY(-50%) !important'
                : 'none !important',
              ...(isNew && {
                top: '50% !important',
                right: '0 !important',
                width: '18px',
                height: '18px',
                pointerEvents: 'auto !important',
                cursor: 'pointer',
              }),
            },
            ...(isNew && {
              width: 'fit-content',
              minWidth: 'fit-content',
              flexShrink: 0,
              height: '100%',
              minHeight: 'unset',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent !important',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
            }),
          }}
          renderValue={(selected: any) => {
            const value = selected || initialSelectedDate;
            if (!value) return '';

            if (initialSelectedDate === 'customDateRange') {
              setFilterMonth('customDateRange');
              if (startingDate && endingDate && moment(startingDate).isSame(endingDate, 'day')) {
                return moment(startingDate).format('DD/MM/YYYY');
              }
              return 'Custom Date Range';
            }
            const tokenMap: Record<string, string> = {
              Today: 'Today',
              Hrs: 'Yesterday',
              '7d': 'Last 7 days',
              '30d': 'Last 30 days',
              '3M': 'Last 3 Months',
              custom: isDbuSql ? 'YTD' : 'Custom Date Range',
              customDateRange: 'Custom Date Range',
            };

            return tokenMap[value] ?? value;
          }}
          MenuProps={{
            PaperProps: {
              sx: isNew
                ? {
                    borderRadius: '4px',
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                    color: `${colors.DropdownV2.menuItemText} !important`,
                    '& .MuiMenuItem-root': {
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      color: `${colors.DropdownV2.menuItemText} !important`,
                      '&:hover': {
                        backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                      },
                      '&.Mui-focusVisible': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                      },
                      '&.Mui-selected': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                      },
                    },
                  }
                : {
                    backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
                    borderRadius: '4px',
                  },
            },
            MenuListProps: {
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
          }}
        >
          {!isDbuSql && (
            <MenuItem value={'Today'} sx={resolvedMenuItemSx}>
              {renderMenuLabel('Today', 'Today')}
            </MenuItem>
          )}
          {filterMonth.includes('-') && filterMonth !== moment().format('YYYY-MM-DD') && (
            <MenuItem
              value={moment(filterMonth).format('YYYY-MM-DD')}
              sx={resolvedMenuItemSx}
            >
              {renderMenuLabel(
                moment(filterMonth).format('YYYY-MM-DD'),
                moment(filterMonth).format('MM/DD/YYYY')
              )}
            </MenuItem>
          )}

          <MenuItem value={'Hrs'} sx={resolvedMenuItemSx}>
            {renderMenuLabel('Hrs', 'Yesterday')}
          </MenuItem>
          <MenuItem value={'7d'} sx={resolvedMenuItemSx}>
            {renderMenuLabel('7d', 'Last 7 days')}
          </MenuItem>
          <MenuItem value={'30d'} sx={resolvedMenuItemSx}>
            {renderMenuLabel('30d', 'Last 30 days')}
          </MenuItem>
          <MenuItem value={'3M'} sx={resolvedMenuItemSx}>
            {renderMenuLabel('3M', 'Last 3 Months')}
          </MenuItem>
          {(!isDbuSql || (isDbuSql && hasYTD)) && (
            <MenuItem value="custom" sx={resolvedMenuItemSx}>
              {renderMenuLabel(
                'custom',
                isDbuSql ? 'YTD' : 'Custom Date Range'
              )}
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {isCustomMonth.current && (
        <Popover
          open={dateFilterMenu !== null}
          onClose={handleDateFilterMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            dateFilterMenu !== null
              ? { top: dateFilterMenu.mouseY, left: dateFilterMenu.mouseX }
              : undefined
          }
        >
          <Box
            sx={{
              '.rdp-button:hover:not([disabled]):not(.rdp-day_selected)': {
								backgroundColor: '#808284ff',
							},
              '.rdrCalendarWrapper': {
                backgroundColor: '#003771',
                borderRadius: '4px',
                padding: '20px',
              },
              '.rdrDateDisplayWrapper': {
                display: 'none',
              },
              '.rdrMonthAndYearWrapper': {
                position: 'relative',
                display: 'block',
                marginBottom: '10px',
              },
              '.rdrMonthAndYearPickers': {
                display: 'block',
                height: '30px',
                '& select': {
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: 700,
                  padding: '3px 0',
                  '& option': {
                    background: '#001e3e',
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: 500,
                    padding: '4px 0',
                    '&:focus, &:active': {
                      outline: 'none',
                      border: 'none',
                    },
                  },
                },
              },
              '.rdrNextPrevButton': {
                border: '1px solid #71758F',
                background: 'none',
                borderRadius: '4px',
                position: 'absolute',
                right: 0,
                top: 0,
                padding: '4px 7px',
                '&.rdrPprevButton': {
                  right: '35px',
                  '& i': {
                    border: 'solid #e5e5e5',
                    borderWidth: '0 2px 2px 0',
                    display: 'inline-block',
                    padding: '3px',
                    transform: 'rotate(-135deg)',
                    verticalAlign: 'middle',
                  },
                },
                '&.rdrNextButton': {
                  '& i': {
                    border: 'solid #e5e5e5',
                    borderWidth: '0 2px 2px 0',
                    display: 'inline-block',
                    padding: '3px',
                    transform: 'rotate(45deg)',
                    verticalAlign: 'text-top',
                    marginTop: '2px',
                  },
                },
                '&:hover': {
                  backgroundColor: '#002349',
                },
              },
              '.rdrMonths': {
                '.rdrMonth': {
                  width: '16em',
                },
                '.rdrWeekDays': {
                  marginBottom: '10px',
                  '.rdrWeekDay': {
                    fontFamily: 'Montserrat',
                    color: '#71758F',
                    fontSize: '14px',
                    fontWeight: 600,
                  },
                },
                '.rdrSelected, .rdrInRange, .rdrStartEdge, .rdrEndEdge': {
                  background: '#009BD8',
                  position: 'absolute',
                  top: '5px',
                  left: 0,
                  right: 0,
                  bottom: '5px',
                },
                '.rdrInRange': {
                  backgroundColor: 'rgba(113, 117, 143, 0.4)',
                },
                '.rdrStartEdge': {
                  borderTopLeftRadius: '1.042em',
                  borderBottomLeftRadius: '1.042em',
                  left: '2px',
                },
                '.rdrEndEdge': {
                  borderTopRightRadius: '1.042em',
                  borderBottomRightRadius: '1.042em',
                  right: '2px',
                },
                '.rdrDayNumber': {
                  '& span': {
                    color: '#fff',
                  },
                },
                '.rdrDayToday': {
                  '&:after': {
                    content: "''",
                    position: 'absolute',
                    bottom: '4px',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    width: '18px',
                    height: '2px',
                    borderRadius: '2px',
                    background: '#3d91ff',
                  },
                },
                '.rdrDays': {
                  '.rdrDay': {
                    backgroundColor: '#003771',
                    border: 'none',
                    fontFamily: 'Montserrat',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '13px 0',
                  },
                  '.rdrDayPassive': {
                    '.rdrDayNumber': {
                      '& span': {
                        color: 'rgba(113, 117, 143, 1)',
                      },
                    },
                  },
                },
              },
            }}
          >
            <DayPicker
              mode="range"
              selected={{
                from: selectionRange?.startDate ?? undefined,
                to: selectionRange?.endDate ?? undefined,
              }}
              onSelect={(value: any) => {
                handleSelectDateRange({
                  selection: {
                    startDate: value?.from ?? null,
                    endDate: value?.to ?? null,
                    key: 'selection',
                  },
                });
              }}
              locale={locale}
            />
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default ReactDateRangeFilter;
